import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CaseloadOverview from './components/CaseloadOverview';
import StudentProfileCard from './components/StudentProfileCard';
import FilterPanel from './components/FilterPanel';
import CommunicationTools from './components/CommunicationTools';
import BulkReviewPanel from './components/BulkReviewPanel';
import { useAuth } from '../../contexts/AuthContext';
import { getCounselorStudents, getStudentCompleteData, calculateEngagementScore, calculateProgressPercentage } from '../../services/userService';

const CounselorManagementPortal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState('caseload');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [caseload, setCaseload] = useState([]);
  const [filters, setFilters] = useState({
    priorityLevel: 'all',
    performanceRange: 'all',
    engagementLevel: 'all',
    interventionRequired: false
  });

  useEffect(() => {
    const fetchCaseloadData = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      try {
        const { data: counselorStudentsData } = await getCounselorStudents(user?.id);
        
        if (counselorStudentsData && counselorStudentsData?.length > 0) {
          const caseloadList = await Promise.all(
            counselorStudentsData?.map(async (cs) => {
              const studentData = await getStudentCompleteData(cs?.student?.id);
              
              const engagementScore = calculateEngagementScore(
                studentData?.quizResults,
                studentData?.achievements
              );
              
              const performanceScore = calculateProgressPercentage(
                studentData?.quizResults,
                studentData?.achievements
              );
              
              const lastActivity = studentData?.quizResults?.[0]?.completed_at || cs?.created_at;
              const daysSinceActivity = Math.floor(
                (new Date() - new Date(lastActivity)) / (1000 * 60 * 60 * 24)
              );
              
              return {
                id: cs?.student?.id,
                name: cs?.student?.full_name || 'Étudiant',
                grade: cs?.student?.grade || 'Non spécifié',
                priorityLevel: cs?.priority_level || 'medium',
                performanceScore,
                engagementLevel: engagementScore > 70 ? 'high' : engagementScore > 40 ? 'medium' : 'low',
                lastActivity,
                interventionRequired: daysSinceActivity > 3 || engagementScore < 40,
                recommendationAccuracy: Math.min(performanceScore + 10, 100),
                quizCompletion: studentData?.quizResults?.length || 0,
                parentCommunication: 'active',
                recentAlert: daysSinceActivity > 3 ? `Aucune activité depuis ${daysSinceActivity} jours` : null
              };
            })
          );
          
          setCaseload(caseloadList);
        }
      } catch (error) {
        console.error('Error fetching caseload data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseloadData();
  }, [user?.id]);

  const filteredCaseload = caseload?.filter(student => {
    if (filters?.priorityLevel !== 'all' && student?.priorityLevel !== filters?.priorityLevel) return false;
    if (filters?.performanceRange !== 'all') {
      const [min, max] = filters?.performanceRange?.split('-')?.map(Number);
      if (student?.performanceScore < min || student?.performanceScore > max) return false;
    }
    if (filters?.engagementLevel !== 'all' && student?.engagementLevel !== filters?.engagementLevel) return false;
    if (filters?.interventionRequired && !student?.interventionRequired) return false;
    return true;
  });

  const handleStudentSelect = (studentId) => {
    const student = caseload?.find(s => s?.id === studentId);
    setSelectedStudent(student);
    setViewMode('student-detail');
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleGenerateReport = () => {
    console.log('Génération du rapport institutionnel');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar />
        <div className="content-area">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Chargement des données...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (caseload?.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar />
        <div className="content-area">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="text-center py-12">
              <Icon name="Users" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-foreground mb-2">Aucun étudiant assigné</h2>
              <p className="text-muted-foreground mb-6">Vous n'avez pas encore d'étudiants dans votre liste de suivi.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar />
      <div className="content-area">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Portail de Gestion Conseiller
                </h1>
                <p className="text-sm md:text-base text-gray-600">
                  Gérez vos cas étudiants avec des outils professionnels de suivi et d'intervention
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  iconName="FileText"
                  iconPosition="left"
                  onClick={handleGenerateReport}
                >
                  Rapport Institutionnel
                </Button>
                <Button
                  variant="default"
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={() => setViewMode('communication')}
                >
                  Sessions de Groupe
                </Button>
              </div>
            </div>
          </div>

          {/* View Mode Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <div className="flex gap-4">
              <button
                onClick={() => setViewMode('caseload')}
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  viewMode === 'caseload' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon name="Users" size={18} className="inline mr-2" />
                Vue d'Ensemble
              </button>
              <button
                onClick={() => setViewMode('student-detail')}
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  viewMode === 'student-detail' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                disabled={!selectedStudent}
              >
                <Icon name="User" size={18} className="inline mr-2" />
                Profil Étudiant
              </button>
              <button
                onClick={() => setViewMode('communication')}
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  viewMode === 'communication' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon name="MessageSquare" size={18} className="inline mr-2" />
                Communication
              </button>
            </div>
          </div>

          {/* Content Area */}
          {viewMode === 'caseload' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-1">
                <FilterPanel filters={filters} onFilterChange={handleFilterChange} />
              </div>
              <div className="lg:col-span-3">
                <CaseloadOverview
                  caseload={filteredCaseload}
                  onStudentSelect={handleStudentSelect}
                />
              </div>
            </div>
          )}

          {viewMode === 'student-detail' && selectedStudent && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <StudentProfileCard student={selectedStudent} />
              </div>
              <div className="lg:col-span-1">
                <BulkReviewPanel studentId={selectedStudent?.id} />
              </div>
            </div>
          )}

          {viewMode === 'communication' && (
            <CommunicationTools caseload={caseload} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CounselorManagementPortal;